import { useEffect } from 'react'
import { useBackgroundJobStore } from '../stores/backgroundJobStore'

/**
 * アプリ全体でマウントされるバックグラウンドポーラー。
 * backgroundJobStore の activeJobs を5秒ごとにポーリングし、
 * 完了・エラー時にストアからジョブを削除する。
 * StoryboardPage / AnalyzingPage が同じ projectId を処理中の場合はスキップする。
 */
export function useBackgroundPoller() {
  const jobs = useBackgroundJobStore(s => s.jobs)
  const mountedStoryboardProjectId = useBackgroundJobStore(s => s.mountedStoryboardProjectId)
  const mountedAnalyzingProjectId = useBackgroundJobStore(s => s.mountedAnalyzingProjectId)
  const updateJob = useBackgroundJobStore(s => s.updateJob)
  const removeJob = useBackgroundJobStore(s => s.removeJob)

  const activeJobIds = Object.keys(jobs).filter(id => jobs[id].status === 'processing')

  useEffect(() => {
    if (activeJobIds.length === 0) return

    const poll = async () => {
      for (const projectId of activeJobIds) {
        const job = jobs[projectId]
        const isAnalysis = job?.type === 'analysis'

        // 対応するページが現在マウント中ならスキップ（ページ自身がポーリング中）
        if (!isAnalysis && mountedStoryboardProjectId === projectId) continue
        if (isAnalysis && mountedAnalyzingProjectId === projectId) continue

        try {
          const endpoint = isAnalysis
            ? `/api/projects/${projectId}/analyze`
            : `/api/projects/${projectId}/storyboard/status`

          const res = await fetch(endpoint, { credentials: 'include' })
          // プロジェクト削除(404)・権限なし(401/403)のジョブは二度と進まない。
          // 残すと localStorage に最大24時間残り、サイドバーに「生成中」が出続けて無駄なリクエストを送り続ける
          if (res.status === 404 || res.status === 401 || res.status === 403) {
            removeJob(projectId)
            continue
          }
          if (!res.ok) continue
          const data = await res.json()
          if (!data.success) continue

          const { status, step, progress, message, error } = data.data

          const isCompleted = isAnalysis
            ? status === 'completed'
            : (status === 'completed' || step === 'ready_for_template' || step === 'completed')
          const isError = status === 'error'

          if (isCompleted) {
            updateJob(projectId, { status: 'completed', progress: 100, message: '完了しました' })
            setTimeout(() => removeJob(projectId), 3000)
          } else if (isError) {
            updateJob(projectId, { status: 'error', message: error || 'エラーが発生しました' })
            setTimeout(() => removeJob(projectId), 5000)
          } else {
            updateJob(projectId, {
              progress: progress ?? 0,
              message: message ?? '',
            })
          }
        } catch {
          // ポーリングエラーは無視して次回リトライ
        }
      }
    }

    poll()
    const interval = setInterval(poll, 5000)
    return () => clearInterval(interval)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [JSON.stringify(activeJobIds), mountedStoryboardProjectId, mountedAnalyzingProjectId])
}
